import { FC, ReactNode } from 'react';

import { Modal } from '@/components/Modal/Modal';
import { Button } from '@/components/ui/Button/Button';
import { CrossIcon, LeftArrowIcon } from '@/components/ui/Icons/Icons';
import { useDrawerPosition } from '@/hooks/useDrawerPosition';

import { TypographyVariant } from '../ui/Typography/Typography.types';
import * as S from './DrawerModal.styles';

export type DrawerModalProps = {
  title: string;
  isOpen: boolean;
  submitLabel?: string;
  onBack?: () => void;
  onClose: () => void;
  formId?: string;
  children: ReactNode;
  submitOnClose?: boolean;
  titleVariant?: TypographyVariant;
  isCenterTitle?: boolean;
};

export const DrawerModal: FC<DrawerModalProps> = ({
  isOpen,
  onBack,
  onClose,
  title,
  submitLabel,
  formId,
  children,
  submitOnClose,
  titleVariant = 'h5',
  isCenterTitle
}) => {
  const { modalRef, headerRef, footerRef, contentRef, handleClose } =
    useDrawerPosition({
      onClose,
      isOpen
    });

  const shouldRenderFooter = isOpen && !!submitLabel && !!formId;

  return (
    <>
      <Modal
        ref={modalRef}
        onClose={handleClose}
        position="bottom"
        isOpen={isOpen}
        transition="transform, 0.3s"
      >
        <S.Wrapper>
          <S.HeaderWrapper
            ref={headerRef}
            $isBack={!!onBack}
            $isCenter={isCenterTitle}
          >
            {!!onBack && (
              <S.BackButton
                onClick={onBack}
                className="back-button"
                data-testid="back-button"
              >
                <LeftArrowIcon />
              </S.BackButton>
            )}

            <S.DrawerTitle variant={titleVariant}>{title}</S.DrawerTitle>

            <S.CrossButton onClick={handleClose} aria-label="close-button">
              <CrossIcon />
            </S.CrossButton>
          </S.HeaderWrapper>

          <S.ContentWrapper ref={contentRef}>{children}</S.ContentWrapper>
        </S.Wrapper>
      </Modal>

      {shouldRenderFooter && (
        <S.FooterWrapper ref={footerRef}>
          <Button
            onClick={submitOnClose ? handleClose : undefined}
            variant="primary"
            form={formId}
            type="submit"
            fullWidth
            data-testid="footer-submit-button"
          >
            {submitLabel}
          </Button>
        </S.FooterWrapper>
      )}
    </>
  );
};
